<template>
  <nav class="navbar" :class="{ 'scrolled': isScrolled, 'navbar-hidden': !isVisible }">
    <div class="container">
      <div class="navbar-content">
        <!-- Logo -->
        <div class="navbar-brand">
          <router-link to="/" class="brand-link">
            <img v-if="companyInfo && companyInfo.logoUrl" :src="companyInfo.logoUrl" alt="Logo" class="logo">
            <span class="brand-text">{{ companyInfo ? companyInfo.companyName : '倾至城科技' }}</span>
          </router-link>
        </div>

        <!-- 导航菜单 -->
        <div class="navbar-menu" :class="{ 'is-active': mobileMenuOpen }">
          <router-link to="/" class="nav-link" @click="closeMobileMenu">首页</router-link>
          <router-link to="/about" class="nav-link" @click="closeMobileMenu">关于我们</router-link>
          <router-link to="/products" class="nav-link" @click="closeMobileMenu">产品服务</router-link>
          <router-link to="/news" class="nav-link" @click="closeMobileMenu">新闻动态</router-link>
          <router-link to="/contact" class="nav-link" @click="closeMobileMenu">联系我们</router-link>
        </div>

        <!-- 移动端菜单按钮 -->
        <div class="navbar-burger" @click="toggleMobileMenu">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapState } from 'vuex'
import { preloadImage, handleImageError } from '@/utils/imageUtils'

export default {
  name: 'NavBar',
  props: {
    hideOnLoad: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      mobileMenuOpen: false,
      isScrolled: false,
      isVisible: true
    }
  },
  computed: {
    ...mapState(['companyInfo'])
  },
  mounted () {
    // 监听滚动以添加类名切换阴影与底色
    this.handleScroll()
    window.addEventListener('scroll', this.handleScroll, { capture: true, passive: true })

    // 控制初次载入时的隐藏
    if (this.hideOnLoad) {
      const docScroll = window.scrollY || window.pageYOffset || (document.documentElement && document.documentElement.scrollTop) || (document.body && document.body.scrollTop) || 0
      this.isVisible = docScroll > 10
    }
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll, { capture: true })
  },
  methods: {
    async getCompanyLogoUrl () {
      if (!this.companyInfo || !this.companyInfo.logoUrl) {
        return '/images/logo-default.png'
      }
      return await preloadImage(this.companyInfo.logoUrl, 'company')
    },
    toggleMobileMenu () {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenu () {
      this.mobileMenuOpen = false
    },
    handleImageError (event) {
      return handleImageError(event, 'company')
    },
    handleScroll (e) {
      // 兼容可能在某些子容器（如 #app 或 custom-wrapper）中滚动的情况
      const targetScroll = (e && e.target && typeof e.target.scrollTop === 'number') ? e.target.scrollTop : 0
      const docScroll = window.scrollY || window.pageYOffset || (document.documentElement && document.documentElement.scrollTop) || (document.body && document.body.scrollTop) || 0
      const currentScroll = Math.max(docScroll, targetScroll)

      this.isScrolled = currentScroll > 15

      // 页面滚动时如果启用隐藏，则动态显示或隐藏菜单
      if (this.hideOnLoad) {
        this.isVisible = currentScroll > 10
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid $border-light;
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), background 0.3s, box-shadow 0.3s;

  // 隐藏状态类，向上滑动移出屏幕
  &.navbar-hidden {
    transform: translateY(-100%);
  }

  // 滚动高亮底色与阴影
  &.scrolled {
    background: rgba(255, 255, 255, 0.98);
    box-shadow: $box-shadow-light;
  }
}

.navbar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
}

.navbar-brand {
  .brand-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: $text-primary;

    .logo {
      height: 40px;
      margin-right: 12px;
    }

    .brand-text {
      font-size: $font-size-large;
      font-weight: 600;
      color: $primary-color;
    }
  }
}

.navbar-menu {
  display: flex;
  align-items: center;
  gap: 32px;

  .nav-link {
    text-decoration: none;
    color: $text-primary;
    font-weight: 500;
    padding: 8px 0;
    position: relative;
    transition: $transition-base;

    &:hover {
      color: $primary-color;
    }

    &.router-link-active {
      color: $primary-color;

      &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 2px;
        background: $primary-color;
        border-radius: 1px;
      }
    }
  }
}

.navbar-burger {
  display: none;
  flex-direction: column;
  cursor: pointer;

  span {
    width: 25px;
    height: 3px;
    background: $text-primary;
    margin: 3px 0;
    transition: $transition-base;
    border-radius: 2px;
  }
}

// 移动端样式
@media (max-width: 768px) {
  .navbar-burger {
    display: flex;
  }

  .navbar-menu {
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: white;
    flex-direction: column;
    padding: 20px;
    box-shadow: $box-shadow-light;
    transform: translateY(-100%);
    opacity: 0;
    visibility: hidden;
    transition: $transition-md-fade;

    &.is-active {
      transform: translateY(0);
      opacity: 1;
      visibility: visible;
    }

    .nav-link {
      width: 100%;
      text-align: center;
      padding: 12px 0;
      border-bottom: 1px solid $border-light;

      &:last-child {
        border-bottom: none;
      }
    }
  }
}
</style>
